import React, { useEffect, useState, useCallback } from 'react'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import type { StyleClasses } from '@paintscout/ui'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import RefreshIcon from '@material-ui/icons/Refresh'
import SettingsIcon from '@material-ui/icons/Settings'
import UpdateIcon from '@material-ui/icons/SystemUpdate'
import { Avatar, ConfirmationDialog, IconButton, PopupMenu, PopupMenuItem, useDialogs, useUser } from '@paintscout/ui'
import { Link } from 'react-router-dom'
import Role from '../Role'

const useStyles = makeStyles<Theme, ProfileButtonProps>(
  (theme) => ({
    root: {
      color: theme.palette.common.black
    },
    iconButton: {
      color: 'inherit',
      padding: theme.spacing(1)
    },
    icon: {
      fontSize: '1.5em',
      [theme.breakpoints.down('sm')]: {
        fontSize: '1.25em'
      }
    },
    updateMenuItem: {
      color: theme.palette.primary.main,
      fontWeight: theme.typography.fontWeightMedium
    },
    anchor: {
      textDecoration: 'none',
      '&:focus': {
        outline: 'none'
      },
      color: theme.palette.common.black
    },
    headerProfileImage: {
      width: '1.4em',
      height: '1.4em',
      borderRadius: '50%',
      objectFit: 'cover'
    },
    profileImage: {
      '& img': {
        borderRadius: '50%',
        objectFit: 'cover'
      }
    },
    newIcon: {
      marginLeft: theme.spacing(1)
    },
    profileAvatar: {
      marginRight: theme.spacing(2)
    }
  }),
  { name: 'ProfileButton' }
)

export interface ProfileButtonProps {
  classes?: StyleClasses<typeof useStyles>
}

function ProfileButton(props: ProfileButtonProps) {
  const classes = useStyles(props)
  const { openDialog, dismissDialog } = useDialogs()
  const { user, isTrial, trialExpired, isChurned } = useUser()
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false)

  const onUpdateReady = useCallback((_: any) => {
    setIsUpdateAvailable(true)
  }, [])

  useEffect(() => {
    window.addEventListener('UPDATE_READY', onUpdateReady)
    return () => {
      window.removeEventListener('UPDATE_READY', onUpdateReady)
    }
  }, [onUpdateReady])

  const restart = useCallback(() => {
    window.location.reload()
  }, [])

  const renderPopupComponent = useCallback(() => {
    const userProfilePictureUrl = user?.user_metadata?.customAvatar?.value
    const userName = user?.name || user?.email
    const userColor = user?.user_metadata?.color

    const Icon = userProfilePictureUrl ? (
      <img src={userProfilePictureUrl} className={classes.headerProfileImage} />
    ) : (
      <Avatar
        backgroundColor="#FFFFFF"
        disableTooltip
        filled
        avatarColor={userColor}
        width="30px"
        height="30px"
        fontSize="13px"
      >
        {userName.toUpperCase().split(/\s+/).slice(0, 2).join(' ')}
      </Avatar>
    )

    return (
      <IconButton classes={{ root: classes.iconButton }} data-testid="profileButton">
        {Icon}
      </IconButton>
    )
  }, [user, classes])

  const userProfilePictureUrl = user?.user_metadata?.customAvatar?.value
  const userName = user?.name || user?.email
  const userColor = user?.user_metadata?.color

  const restartOnlyMenu = (
    <>
      <PopupMenuItem icon={RefreshIcon} onClick={restart}>
        Restart
      </PopupMenuItem>
      <Link to="/logout" className={classes.anchor}>
        <PopupMenuItem icon={ArrowBackIcon}>Log Out</PopupMenuItem>
      </Link>
    </>
  )

  const fullMenu = (
    <>
      {isUpdateAvailable && (
        <PopupMenuItem
          classes={{ root: classes.updateMenuItem }}
          icon={UpdateIcon}
          onClick={() => {
            openDialog(ConfirmationDialog, {
              title: 'Update',
              message: (
                <div>
                  The page will be reloaded to finish the update. Any unsaved data will be lost!
                  <br />
                  <br />
                  Is this OK?
                </div>
              ),
              onConfirm: () => {
                openDialog(
                  ConfirmationDialog,
                  {
                    title: 'Update',
                    message: <strong>Please make sure any other PaintScout tabs are closed, and then click OK</strong>,
                    onConfirm: () => window.location.reload(),
                    onCancel: dismissDialog
                  },
                  {
                    replace: true
                  }
                )
              },
              onCancel: dismissDialog
            })
          }}
        >
          Update Available
        </PopupMenuItem>
      )}
      <Link to="/profile" className={classes.anchor}>
        {userProfilePictureUrl ? (
          <PopupMenuItem classes={{ root: classes.profileImage }} image={userProfilePictureUrl}>
            Your Profile
          </PopupMenuItem>
        ) : (
          <PopupMenuItem
            icon={() => (
              <Avatar
                backgroundColor="#FFFFFF"
                disableTooltip
                filled
                className={classes.profileAvatar}
                avatarColor={userColor}
                width="24px"
                height="24px"
                fontSize="10px"
                style={{
                  paddingLeft: 0,
                  paddingTop: 0
                }}
              >
                {userName.toUpperCase().split(/\s+/).slice(0, 2).join(' ')}
              </Avatar>
            )}
          >
            Your Profile
          </PopupMenuItem>
        )}
      </Link>
      <Link to="/settings" className={classes.anchor}>
        <Role permission={'write:account-settings'}>
          <PopupMenuItem icon={SettingsIcon}>Account Settings</PopupMenuItem>
        </Role>
      </Link>
      <PopupMenuItem icon={RefreshIcon} onClick={restart}>
        Restart
      </PopupMenuItem>
      <Link to="/logout" className={classes.anchor}>
        <PopupMenuItem icon={ArrowBackIcon}>Log Out</PopupMenuItem>
      </Link>
    </>
  )

  const restrictProfileMenu = (isTrial && trialExpired) || isChurned

  return (
    <>
      <PopupMenu classes={{ root: classes.root }} component={renderPopupComponent()} id="profile">
        {restrictProfileMenu ? restartOnlyMenu : fullMenu}
      </PopupMenu>
    </>
  )
}

export default ProfileButton
